/**
 * AudioEye Widget Block
 *
 * Injection block that loads the AudioEye accessibility widget (`aem.js`)
 * using the property's AudioEye site hash. Surfaces the same config fields
 * the AudioEye Site API exposes (`ToolbarSettingsDto`) so they can be
 * managed per-property and later synced to AudioEye via `UpdateSite`.
 *
 * Runtime vs. management:
 * - `site_hash` is what the loader script needs at runtime to identify
 *   the site and pull its config from the AudioEye platform.
 * - `site_id`, `cta_location`, `cta_color`, `toolbar_theme` mirror the
 *   AudioEye management API. The widget reads its CTA position/color/
 *   theme from the AudioEye platform (not the page), so these tokens
 *   don't override the widget directly — they're the editable source
 *   of truth that a provisioning job will push to
 *   `PUT /v20210921/site/{id}`.
 *
 * See: https://api.audioeye.io/openapi#/
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken, IS_IN_EDITOR_FRAME } from '@vibesites/runtime/react';

const SCRIPT_ID = 'audioeye-widget-script';

// Modern v3 loader served from AudioEye's services CDN. This is the
// snippet the Portal hands out for newly provisioned sites, NOT the
// legacy `wsmcdn.audioeye.com/aem.js` the OpenAPI spec's `installScript`
// field still returns. The new loader handles both `managed` and
// `custom-mode` sites; the legacy loader does not.
const AUDIOEYE_SRC = 'https://wsv3cdn.audioeye-services.com/aem.js';

const DEFAULT_CTA_COLOR = '#1275B3';
const DEFAULT_CTA_LOCATION = 'right-lower';

// AudioEye's six documented CTA position presets (`ToolbarSettingsDto.
// ctaLocation`), mapped to viewport-fixed offsets that match how the real
// launcher paints itself (matched from launcher's own CSS rules, e.g.
// `.ae-cta-position-preset-right-lower { bottom: 18px }`).
const POSITION_STYLES: Record<string, CSSProperties> = {
  'right-lower':     { bottom: 18, right: 18 },
  'right-mid-lower': { bottom: '30%', right: 18 },
  'right-middle':    { top: '50%', right: 18, transform: 'translateY(-50%)' },
  'right-mid-upper': { top: '30%', right: 18 },
  'right-upper':     { top: 18, right: 18 },
  'left-lower':      { bottom: 18, left: 18 },
};

/**
 * Editor-only stand-in for the real AudioEye CTA.
 *
 * AudioEye won't paint its widget on non-production hostnames (the binding
 * is per-domain on AE's side), so the editor preview would otherwise show
 * nothing. This mock mirrors the real launcher's dimensions / shape /
 * positioning so editors can see and adjust placement + color, with a
 * subtle dashed outline + "preview" tooltip making clear it isn't the
 * real widget. Never rendered on deployed sites.
 *
 * Rendered via a portal to `document.body`. The runtime wraps every block
 * in a `<section>` (see Section in runtime/react/components.tsx), which
 * lives somewhere inside the theme's layout tree. If anywhere in that
 * chain there's a `transform`, `perspective`, `filter`, `contain`, or
 * `will-change`, `position: fixed` re-anchors to that ancestor instead
 * of the viewport — exactly the same CSS spec gotcha that bites the real
 * AudioEye `ae_app` container on some sites. Portaling to `body` keeps
 * us out of any theme/layout ancestor chain.
 */
function AudioeyeWidgetMockCta({ color, location }: { color: string; location: string }) {
  const pos = POSITION_STYLES[location] ?? POSITION_STYLES[DEFAULT_CTA_LOCATION];
  console.log('AudioeyeWidgetMockCta', color, location);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="img"
      aria-label="AudioEye Widget (preview only)"
      title="AudioEye Widget — preview only. The real widget will load on the deployed site."
      style={{
        position: 'fixed',
        width: 44,
        height: 44,
        borderRadius: '50%',
        backgroundColor: color,
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
        outline: '2px dashed rgba(255, 255, 255, 0.55)',
        outlineOffset: -6,
        zIndex: 20000,
        ...pos,
      }}
    >
      <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <circle cx="12" cy="3.5" r="1.75" />
        <path d="M20.5 7.5c-2.6.65-5.55.95-8.5.95s-5.9-.3-8.5-.95L3 9.45c1.86.5 4 .83 6 1V23h2v-6h2v6h2V10.45c2-.17 4.14-.5 6-1l-.5-1.95z" />
      </svg>
    </div>,
    document.body,
  );
}

function AudioeyeWidgetComponent(_props: BlockProps) {
  const siteHash = useBlockToken('site_hash', {
    defaultField: 'custom_audioeye_site_hash',
  });
  const ctaColor = useBlockToken('cta_color', {
    defaultField: 'custom_audioeye_cta_color',
    defaultValue: DEFAULT_CTA_COLOR,
  });
  const ctaLocation = useBlockToken('cta_location', {
    defaultField: 'custom_audioeye_cta_location',
    defaultValue: DEFAULT_CTA_LOCATION,
  });

  useEffect(() => {
    // In the editor preview we never inject AudioEye's loader. AE binds
    // sites per-domain, so the preview hostname can't verify and the
    // request would either fail or steal a verification slot from the
    // real site. The mock below stands in for visual feedback.
    if (IS_IN_EDITOR_FRAME) return;
    if (!siteHash) return;

    const w = window as Window & { __AudioEyeSiteHash?: string };

    // Singleton: AudioEye's loader is itself singleton-safe, but we also
    // skip re-inserting the script tag so we don't paint two widgets if
    // the block re-mounts (StrictMode, SPA navigation, etc.).
    if (w.__AudioEyeSiteHash === siteHash && document.getElementById(SCRIPT_ID)) return;

    w.__AudioEyeSiteHash = siteHash;
    if (document.getElementById(SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.type = 'text/javascript';
    script.async = true;
    script.crossOrigin = 'anonymous';
    // Hash is also passed as a query param so the CDN/edge can identify
    // the site before any JS executes (CSP-friendly, cache-friendly).
    script.src = `${AUDIOEYE_SRC}?h=${encodeURIComponent(siteHash)}`;
    document.body.appendChild(script);

    // No teardown: the AudioEye script paints widget DOM directly in
    // <body> outside our tracking. Removing it leaves orphan nodes and
    // re-injection duplicates the widget.
  }, [siteHash]);

  if (IS_IN_EDITOR_FRAME) {
    return (
      <AudioeyeWidgetMockCta
        color={ctaColor || DEFAULT_CTA_COLOR}
        location={ctaLocation || DEFAULT_CTA_LOCATION}
      />
    );
  }

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'AudioeyeWidget',
  displayName: 'AudioEye Widget',
  tokens: {
    site_hash: {
      label: 'Site Hash',
      type: 'text',
      defaultField: 'custom_audioeye_site_hash',
      suggestedFields: ['custom_audioeye_site_hash'],
      description:
        'AudioEye site hash (the `hash` field returned by `CreateSite`). Required — the loader uses this to identify the site.',
    },
    site_id: {
      label: 'Site ID',
      type: 'text',
      defaultField: 'custom_audioeye_site_id',
      suggestedFields: ['custom_audioeye_site_id'],
      description:
        'AudioEye site id (the `id` UUID returned by `CreateSite`). Used by the management API (`GET/PUT/DELETE /v20210921/site/{id}`); not read at runtime.',
    },
    cta_location: {
      label: 'CTA Location',
      type: 'text',
      defaultField: 'custom_audioeye_cta_location',
      suggestedFields: ['custom_audioeye_cta_location'],
      description:
        'Position of the AudioEye CTA button. One of: right-lower, right-mid-lower, right-middle, right-mid-upper, right-upper, left-lower. Synced to AudioEye via `config.ctaLocation`.',
    },
    cta_color: {
      label: 'CTA Color',
      type: 'color',
      defaultField: 'custom_audioeye_cta_color',
      suggestedFields: ['custom_audioeye_cta_color', 'property_primary_color'],
      description:
        'CSS color for the AudioEye CTA button (e.g. `#1275B3`). Synced to AudioEye via `config.ctaColor`.',
    },
    toolbar_theme: {
      label: 'Toolbar Theme',
      type: 'text',
      defaultField: 'custom_audioeye_toolbar_theme',
      suggestedFields: ['custom_audioeye_toolbar_theme'],
      description:
        'AudioEye Toolbar theme. One of: light, dark. Synced to AudioEye via `config.toolbarTheme`.',
    },
  },
};

export const audioeyeWidgetBlock: BlockDefinition = {
  id: 'audioeye-widget',
  name: 'AudioEye Widget',
  description:
    'Loads the AudioEye accessibility widget on every page using the site hash. CTA position, color, and theme mirror the AudioEye Site API and are intended to be pushed to AudioEye via `PUT /v20210921/site/{id}`.',
  category: 'Integrations',
  placement: 'injection',
  defaultScope: 'template',
  component: AudioeyeWidgetComponent,
  contentSchema,
  tags: ['audioeye', 'accessibility', 'ada', 'compliance', 'widget', 'toolbar'],
};
